import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { print } from "graphql";

import { graphql } from "lib/generated/app-service-gql";
import {
  LeadContactSuggestionsStatusSubscription,
  LeadContactSuggestionsStatusSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { useSubscription } from "lib/hooks/api/useGraphQlClient";

const leadContactSuggestionsStatusSubscriptionDoc = graphql(`
  subscription leadContactSuggestionsStatus($leadId: String!) {
    leadContactSuggestionsStatus(leadId: $leadId) {
      leadId
      status
      suggestionCount
    }
  }
`);

const leadContactSuggestionsStatusSubscriptionString = print(
  leadContactSuggestionsStatusSubscriptionDoc,
);

/**
 * Streams the status of a lead's contact-suggestion generation. On a terminal status the lead query
 * is invalidated so the freshly-persisted suggestions are refetched.
 *
 * `isGenerating` lets the UI show progress while the workflow runs — without it, a lead created
 * moments ago shows an empty contacts section and then suddenly fills in. The backend persists the
 * status for 10 minutes, so a client opening the lead just after creation still receives it.
 *
 * `markGenerationRequested` covers the gap when the user asks for suggestions themselves: the
 * mutation resolves once the run is dispatched, a moment before the workflow publishes its first
 * `running` status, so without it the UI would flicker back to the empty state in between. The
 * pending flag clears on the run's terminal status, which also covers a run that finds nothing.
 */
export function useLeadContactSuggestionsStatus(leadId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const [isGenerationRequested, setIsGenerationRequested] = useState(false);

  const subscription = useSubscription<
    LeadContactSuggestionsStatusSubscription,
    LeadContactSuggestionsStatusSubscriptionVariables
  >(
    leadContactSuggestionsStatusSubscriptionString,
    { leadId },
    {
      enabled: enabled && !!leadId,
      onData: (data) => {
        const event = data.leadContactSuggestionsStatus;
        if (event.status === "completed" || event.status === "failed") {
          setIsGenerationRequested(false);
          void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
        }
      },
    },
  );

  // A pending request belongs to the lead it was made for. Navigating to another lead re-renders
  // this hook with a new `leadId` rather than remounting it, so without this the next lead inherits
  // the spinner and keeps it until a status event arrives for a run it never started.
  useEffect(() => {
    setIsGenerationRequested(false);
  }, [leadId, enabled]);

  const markGenerationRequested = useCallback(() => setIsGenerationRequested(true), []);

  return {
    ...subscription,
    isGenerating:
      isGenerationRequested || subscription.data?.leadContactSuggestionsStatus.status === "running",
    markGenerationRequested,
  };
}
